import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { MDBCard, MDBCardBody, MDBCardHeader, MDBBtn, MDBSpinner } from 'mdb-react-ui-kit'
import { format, parseISO } from 'date-fns'
import { UISelect } from '../../../../components/forms/form-fields'
import UniversityStore from '../../store/University.store'

import './Signup.component.scss'

/**
 * Signup Component
 *
 * Allows agents to register for upcoming USABG University training sessions.
 * Displays a session selector and enrollment button.
 * Hides when user is already enrolled.
 */
const Signup = observer(() => {
  const [selectedSessionId, setSelectedSessionId] = useState('')
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (UniversityStore.sessions.length === 0) {
      UniversityStore.fetchSessions()
    }
  }, [])

  // Don't render if already enrolled
  if (UniversityStore.isEnrolled) {
    return null
  }

  const formatDateRange = (startDate, endDate) => {
    try {
      // Extract just YYYY-MM-DD portion and parse as local date to prevent timezone rollover
      const toLocalDate = dateStr => {
        const dateOnly = String(dateStr).split('T')[0].split(' ')[0]
        const [year, month, day] = dateOnly.split('-').map(Number)
        return new Date(year, month - 1, day)
      }
      const start = toLocalDate(startDate)
      const end = toLocalDate(endDate)
      return `${format(start, 'MMM d')} - ${format(end, 'd, yyyy')}`
    } catch {
      return `${startDate} - ${endDate}`
    }
  }

  const getSessionOptionLabel = session => {
    const dateRange = formatDateRange(session.start_date, session.end_date)
    const location = session.location?.name || 'TBD'
    return `${dateRange} - ${location}`
  }

  const handleEnroll = async () => {
    if (!selectedSessionId) {
      setError('Please select a session')
      return
    }

    setError(null)
    const result = await UniversityStore.enrollInSession(selectedSessionId)

    if (result.success) {
      setSuccess(true)
      // Refresh enrollment data
      await UniversityStore.fetchMyEnrollment()
    } else {
      setError(result.error)
    }
  }

  const availableSessions = UniversityStore.sessions.filter(s => {
    // Filter out full sessions
    if (s.max_capacity) {
      const enrolled = s.enrolled_count || 0
      if (enrolled >= s.max_capacity) return false
    }
    return true
  })

  if (UniversityStore.isLoading.sessions) {
    return (
      <MDBCard id='UniversitySignupComponent'>
        <MDBCardHeader className='signup-header'>
          <i className='fas fa-user-plus mr-2'></i>
          Sign Up for USABG University
        </MDBCardHeader>
        <MDBCardBody className='text-center py-5'>
          <MDBSpinner />
          <p className='mt-3'>Loading available sessions...</p>
        </MDBCardBody>
      </MDBCard>
    )
  }

  if (success) {
    return (
      <MDBCard id='UniversitySignupComponent'>
        <MDBCardHeader className='signup-header'>
          <i className='fas fa-user-plus mr-2'></i>
          Sign Up for USABG University
        </MDBCardHeader>
        <MDBCardBody className='text-center py-5'>
          <div className='success-message'>
            <i className='fas fa-check-circle fa-3x text-success mb-3'></i>
            <h5>Successfully Enrolled!</h5>
            <p className='text-muted'>
              You have been enrolled in the training session. Please complete your deposit payment to secure your spot.
            </p>
          </div>
        </MDBCardBody>
      </MDBCard>
    )
  }

  return (
    <MDBCard id='UniversitySignupComponent'>
      <MDBCardHeader className='signup-header'>
        <i className='fas fa-user-plus mr-2'></i>
        Sign Up for USABG University
      </MDBCardHeader>
      <MDBCardBody>
        {availableSessions.length === 0 ? (
          <div className='no-sessions'>
            <i className='fas fa-calendar-times fa-2x mb-3 text-muted'></i>
            <p>No sessions are currently available for enrollment.</p>
            <p className='text-muted'>Check back later or contact your manager for more information.</p>
          </div>
        ) : (
          <div className='signup-form'>
            <p className='signup-description'>
              Select a training session below and click "Request Enrollment" to begin your USABG University journey. A
              deposit of <strong>${UniversityStore.depositAmount}</strong> is required to secure your spot.
            </p>

            <div className='form-group'>
              <UISelect
                id='sessionSelect'
                name='session_id'
                label='Select Session'
                value={selectedSessionId}
                options={[
                  {
                    value: '',
                    text: '',
                    checked: !selectedSessionId,
                  },
                  ...availableSessions.map(session => ({
                    value: `${session.id}`,
                    text: `${getSessionOptionLabel(session)}${
                      session.max_capacity
                        ? ` (${session.max_capacity - (session.enrolled_count || 0)} seats available)`
                        : ''
                    }`,
                    checked: `${session.id}` === `${selectedSessionId}`,
                  })),
                ]}
                onChange={e => setSelectedSessionId(e.target.value)}
              />
            </div>

            {error && (
              <div className='alert alert-danger'>
                <i className='fas fa-exclamation-circle mr-2'></i>
                {error}
              </div>
            )}

            <div className='signup-actions'>
              <MDBBtn
                type='button'
                color='primary'
                size='lg'
                onClick={handleEnroll}
                disabled={!selectedSessionId || UniversityStore.isLoading.enrolling}
              >
                {UniversityStore.isLoading.enrolling ? (
                  <>
                    <MDBSpinner small className='mr-2' />
                    Enrolling...
                  </>
                ) : (
                  <>
                    <i className='fas fa-user-plus mr-2'></i>
                    Request Enrollment
                  </>
                )}
              </MDBBtn>
            </div>

            <div className='signup-info mt-4'>
              <small className='text-muted'>
                <i className='fas fa-info-circle mr-1'></i>
                By enrolling, you agree to attend the selected training session. A{' '}
                <strong>${UniversityStore.depositAmount}</strong> refundable deposit is required to secure your spot and
                will be refunded upon attendance.
              </small>
            </div>
          </div>
        )}
      </MDBCardBody>
    </MDBCard>
  )
})

export default Signup
